import { useState } from 'react';
import { errorMessage } from '../api/client';
import { employeesApi, usersApi, type EmployeeForm } from '../api/endpoints';
import type { Employee, User } from '../api/types';
import { Badge } from '../components/Badge';
import { Field } from '../components/Field';
import { Modal } from '../components/Modal';
import { Notice } from '../components/Notice';
import {
  EMPLOYEE_STATES,
  EMPLOYEE_STATE_LABELS,
  EMPLOYEE_STATE_TONES,
  type EmployeeState,
} from '../domain/workstations';
import { useAsync } from '../hooks/useAsync';
import { formatDateOnly, fullName } from '../lib/format';

function initialForm(employee: Employee | null): EmployeeForm {
  if (employee === null) {
    return {
      lastName: '',
      firstName: '',
      department: '',
      state: 'actif',
      hiredOn: new Date().toISOString().slice(0, 10),
      leftOn: null,
      userId: null,
    };
  }
  return {
    lastName: employee.lastName,
    firstName: employee.firstName,
    department: employee.department,
    state: employee.state,
    hiredOn: employee.hiredOn,
    leftOn: employee.leftOn,
    userId: employee.userId,
  };
}

function EmployeeModal({
  employee,
  users,
  onClose,
  onSaved,
}: {
  employee: Employee | null;
  users: User[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<EmployeeForm>(() => initialForm(employee));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const patch = (change: Partial<EmployeeForm>): void =>
    setForm((current) => ({ ...current, ...change }));

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const request =
      employee === null
        ? employeesApi.create(form)
        : employeesApi.update(employee.id, form);
    request
      .then(() => onSaved())
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title={employee === null ? 'Nouveau salarié' : `Modifier ${fullName(employee)}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="bouton" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            form="formulaire-salarie"
            className="bouton bouton--primaire"
            disabled={busy}
          >
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </>
      }
    >
      <form id="formulaire-salarie" className="pile pile--serree" onSubmit={onSubmit}>
        {error !== null && <Notice tone="erreur">{error}</Notice>}

        <div className="grille-champs">
          <Field label="Nom">
            <input
              value={form.lastName}
              required
              maxLength={120}
              onChange={(event) => patch({ lastName: event.target.value })}
            />
          </Field>

          <Field label="Prénom">
            <input
              value={form.firstName}
              required
              maxLength={120}
              onChange={(event) => patch({ firstName: event.target.value })}
            />
          </Field>

          <Field label="Service">
            <input
              value={form.department}
              required
              maxLength={120}
              onChange={(event) => patch({ department: event.target.value })}
            />
          </Field>

          <Field label="État">
            <select
              value={form.state}
              onChange={(event) => {
                const state = event.target.value as EmployeeState;
                // un salarié actif n'a pas de date de sortie
                patch({ state, leftOn: state === 'actif' ? null : form.leftOn });
              }}
            >
              {EMPLOYEE_STATES.map((state) => (
                <option key={state} value={state}>
                  {EMPLOYEE_STATE_LABELS[state]}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Date d'entrée">
            <input
              type="date"
              value={form.hiredOn}
              required
              onChange={(event) => patch({ hiredOn: event.target.value })}
            />
          </Field>

          <Field
            label="Date de sortie"
            hint={form.state === 'actif' ? 'Réservée aux salariés partis.' : undefined}
          >
            <input
              type="date"
              value={form.leftOn ?? ''}
              disabled={form.state === 'actif'}
              required={form.state === 'parti'}
              onChange={(event) =>
                patch({ leftOn: event.target.value === '' ? null : event.target.value })
              }
            />
          </Field>

          <Field
            label="Compte utilisateur rattaché"
            hint="Facultatif. Les deux notions restent indépendantes."
            wide
          >
            <select
              value={form.userId ?? ''}
              onChange={(event) =>
                patch({ userId: event.target.value === '' ? null : event.target.value })
              }
            >
              <option value="">Aucun</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName} — {user.email}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </form>
    </Modal>
  );
}

export function AdminEmployeesPage() {
  const [search, setSearch] = useState('');
  const [state, setState] = useState<EmployeeState | ''>('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);

  const users = useAsync((signal) => usersApi.list(signal), []);
  const employees = useAsync(
    (signal) =>
      employeesApi.list(
        {
          recherche: search === '' ? undefined : search,
          etat: state === '' ? undefined : state,
        },
        signal,
      ),
    [search, state],
  );

  const userLabels = new Map(
    (users.data?.users ?? []).map((user) => [user.id, user.displayName]),
  );

  return (
    <div className="pile">
      <div className="entete-page">
        <div>
          <h1>Salariés</h1>
          <p className="entete-page__sous-titre">
            Personnes de l'entreprise, détentrices des postes. Table distincte des
            comptes d'accès.
          </p>
        </div>
        <button
          type="button"
          className="bouton bouton--primaire"
          onClick={() => setCreating(true)}
        >
          Nouveau salarié
        </button>
      </div>

      <div className="outils">
        <Field label="Recherche" hint="Nom, prénom, service.">
          <input
            className="champ--recherche"
            value={search}
            placeholder="Rechercher un salarié…"
            onChange={(event) => setSearch(event.target.value)}
          />
        </Field>
        <Field label="État">
          <select
            value={state}
            onChange={(event) => setState(event.target.value as EmployeeState | '')}
          >
            <option value="">Tous</option>
            {EMPLOYEE_STATES.map((value) => (
              <option key={value} value={value}>
                {EMPLOYEE_STATE_LABELS[value]}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {employees.error !== null && (
        <Notice tone="erreur" title="Chargement impossible">
          {employees.error}
        </Notice>
      )}

      <div className="panneau">
        <div className="tableau-cadre">
          <table className="tableau">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Prénom</th>
                <th>Service</th>
                <th>État</th>
                <th>Entrée</th>
                <th>Sortie</th>
                <th>Compte</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {employees.loading && employees.data === null && (
                <tr>
                  <td colSpan={8} className="tableau__vide">
                    Chargement…
                  </td>
                </tr>
              )}
              {employees.data?.employees.length === 0 && (
                <tr>
                  <td colSpan={8} className="tableau__vide">
                    Aucun salarié ne correspond à ces critères.
                  </td>
                </tr>
              )}
              {employees.data?.employees.map((employee) => (
                <tr key={employee.id}>
                  <td>{employee.lastName}</td>
                  <td>{employee.firstName}</td>
                  <td>{employee.department}</td>
                  <td>
                    <Badge tone={EMPLOYEE_STATE_TONES[employee.state]}>
                      {EMPLOYEE_STATE_LABELS[employee.state]}
                    </Badge>
                  </td>
                  <td className="tableau__nombre">{formatDateOnly(employee.hiredOn)}</td>
                  <td className="tableau__nombre">{formatDateOnly(employee.leftOn)}</td>
                  <td>
                    {employee.userId === null ? (
                      <span className="faible">Aucun</span>
                    ) : (
                      (userLabels.get(employee.userId) ?? employee.userId)
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="bouton bouton--petit"
                      onClick={() => setEditing(employee)}
                    >
                      Modifier
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(creating || editing !== null) && (
        <EmployeeModal
          employee={editing}
          users={users.data?.users ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            employees.reload();
          }}
        />
      )}
    </div>
  );
}
